/**
 * Misquote — Paper Studio design tokens.
 *
 * Aged paper, red ink, deckle edges, tape strips, typewriter eyebrows.
 * Serif display (Bitter) + sans body (Red Hat Display).
 */

import { css } from '@emotion/react';

export const paper = {
    paper:        '#f4ecd8',
    paperWarm:    '#ede2c8',
    paperCool:    '#f8f1de',
    paperBright:  '#fdfaf1',
    paperDeep:    '#ddd1b3',
    ink:          '#1d1a14',
    ink2:         '#2c271e',
    inkDeep:      '#0a0805',
    inkSoft:      '#5a5141',
    inkMute:      '#968a72',
    rule:         '#2f2a20',
    ruleSoft:     'rgba(29,26,20,0.16)',
    red:          '#c8392c',
    redLight:     '#d44434',
    redDeep:      '#9a2a1f',
    sage:         '#708167',
    sticky:       '#f5d27e',
    tape:         'rgba(218, 191, 119, 0.55)',
    tapeRed:      'rgba(200, 57, 44, 0.30)',
} as const;

/**
 * Alpha ramp.
 *
 * The palette above only names colours at full strength, but most of the design
 * is those same colours part-way: deckle edges, dashed rules, inset highlights,
 * type on an ink surface. Without a ramp every one of those gets retyped as a
 * raw `rgba(...)` triple, which is how palette drift starts — so reach for these
 * instead. `shade(0.18)` says "the warm edge brown, faint"; `rgba(60,45,20,0.18)`
 * says nothing.
 *
 * Note these are the *primitives* the design system composes with. Inside ms.tsx
 * the multi-layer shadow stacks are themselves the design statement and stay
 * written out in full; it's component files that should never mix their own.
 */

/** Warm brown — every edge, drop shadow and dashed rule that sits on paper. */
export const shade = (alpha: number) => `rgba(60, 45, 20, ${alpha})`;

/** Paper — type, hairlines and rules sitting on an ink/dark surface. */
export const paperA = (alpha: number) => `rgba(244, 236, 216, ${alpha})`;

/** Ink — hairlines, hover washes and pressed states on paper. */
export const inkA = (alpha: number) => `rgba(29, 26, 20, ${alpha})`;

/** Red ink at part strength — error slips, stamp fills, tape. */
export const redA = (alpha: number) => `rgba(200, 57, 44, ${alpha})`;

/** Top-light: the white inset highlight that makes paper read as lifted. */
export const gloss = (alpha: number) => `rgba(255, 255, 255, ${alpha})`;

/**
 * True black — deliberately *not* `inkA`. Ink is a warm near-black that belongs
 * on paper; this is for the two places paper isn't the substrate: a scrim
 * dimming the page behind a modal, and the shadow an ink-dark surface casts
 * (where warm brown would read as a halo).
 */
export const blackA = (alpha: number) => `rgba(0, 0, 0, ${alpha})`;

/** Sticky-note gold at part strength — the wash behind points and highlights. */
export const stickyA = (alpha: number) => `rgba(245, 210, 126, ${alpha})`;

export const fonts = {
    serif: "'Bitter Variable', Georgia, serif",
    sans:  "'Red Hat Display Variable', system-ui, sans-serif",
} as const;

export const easing = {
    standard: 'cubic-bezier(0.22, 1, 0.36, 1)',
    pop:      'cubic-bezier(0.34, 1.5, 0.5, 1)',
    soft:     'cubic-bezier(0.45, 0, 0.2, 1)',
} as const;

/**
 * Breakpoints.
 *
 * Not a scale — three distinct moments where a layout stops being a phone
 * layout, each earning its keep. Stored as whole media-query strings so they
 * compose directly: `@media ${bp.wide} { … }`.
 */
export const bp = {
    /** Past phone: content columns widen, card grids go multi-column. */
    wide:  '(min-width: 720px)',
    /** The inverse of `wide`, for the odd JS matchMedia check. */
    phone: '(max-width: 720px)',
    /** Modals stop being bottom sheets and centre in the viewport. */
    modal: '(min-width: 768px)',
    /** Room for a two-column split: hero beside card, lobby beside roster. */
    split: '(min-width: 880px)',
} as const;

/**
 * Switch off a looping animation under `prefers-reduced-motion`.
 *
 * index.css collapses animation *durations* to ~0 site-wide, which is right for
 * entrances — they land on their final keyframe (see the note there). A loop has
 * no final keyframe worth landing on, so that same rule would freeze it on an
 * arbitrary frame: a caret stuck invisible, a dot stuck mid-fade. Compose this
 * onto anything running `infinite` so it drops the animation outright and sits
 * in its natural, resting form instead.
 */
export const calmLoop = css`
    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

/**
 * Shared framer-motion spring configs. `pill` is the snappy pop used by
 * top-anchored notifications; `reveal` is the softer settle used when cards
 * enter a list. Kept here so the same feel is reused instead of re-typed.
 */
export const springs = {
    pill:   { type: 'spring' as const, stiffness: 320, damping: 26 },
    reveal: { type: 'spring' as const, stiffness: 220, damping: 24 },
} as const;
